// src/components/layout/Sidebar.tsx

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePlanInfo } from '../../hooks/usePlanInfo';
import {
  HomeIcon,
  UsersIcon,
  PuzzlePieceIcon,
  CogIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Home', href: '/dashboard', icon: HomeIcon },
  { name: 'Accounts', href: '/customers', icon: UsersIcon },
  { name: 'Account Radar', href: '/insights/radar', icon: SignalIcon },
  { name: 'Reports', href: '/qbrs', icon: DocumentTextIcon },
  { name: 'Ask Edyt', href: '/chat', icon: ChatBubbleLeftRightIcon },
  { name: 'Insights', href: '/analytics', icon: ChartBarIcon },
  { name: 'Integrations', href: '/integrations', icon: PuzzlePieceIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
];

interface SidebarProps {
  onMobileMenuClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onMobileMenuClose }) => {
  const location = useLocation();
  const { data: planInfo } = usePlanInfo();
  
  const isAdmin = planInfo?.can_access_admin === true;

  const handleLinkClick = () => {
    // Close mobile menu when a link is clicked
    if (onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  return (
    <div className="flex h-full w-64 flex-col bg-card border-r border-border">
      {/* Logo */}
      <div className="flex h-16 flex-shrink-0 items-center px-4">
        <div className="flex items-center">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
            <span className="text-sm font-bold text-primary-foreground">E</span>
          </div>
          <span className="ml-2 text-xl font-bold text-foreground">Edyt</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-2 py-4">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={handleLinkClick}
              className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium ${
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <item.icon
                className={`mr-3 h-6 w-6 flex-shrink-0 ${
                  isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-accent-foreground'
                }`}
                aria-hidden="true"
              />
              {item.name}
            </Link>
          );
        })}
        
        {/* Billing Link */}
        <Link
          to="/settings/billing"
          onClick={handleLinkClick}
          className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium ${
            location.pathname === '/settings/billing'
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          }`}
        >
          <CreditCardIcon
            className={`mr-3 h-6 w-6 flex-shrink-0 ${
              location.pathname === '/settings/billing' ? 'text-primary' : 'text-muted-foreground group-hover:text-accent-foreground'
            }`}
            aria-hidden="true"
          />
          Billing
        </Link>
        
        {/* Admin Link (only for admin users) */}
        {isAdmin && (
          <Link
            to="/admin"
            onClick={handleLinkClick}
            className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium ${
              location.pathname === '/admin'
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            <ShieldCheckIcon
              className={`mr-3 h-6 w-6 flex-shrink-0 ${
                location.pathname === '/admin' ? 'text-purple-500 dark:text-purple-400' : 'text-muted-foreground group-hover:text-accent-foreground'
              }`}
              aria-hidden="true"
            />
            Admin Panel
          </Link>
        )}
      </nav>

      {/* Footer */}
      <div className="flex flex-shrink-0 border-t border-border p-4">
        <div className="flex items-center text-sm text-muted-foreground">
          <div className="h-2 w-2 rounded-full bg-green-400 mr-2"></div>
          All systems operational
        </div>
      </div>
    </div>
  );
};

export default Sidebar;








